import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CONTACT_INFO } from "../data";
import GalleryLightbox from "../components/GalleryLightbox";
import ContactForm from "../components/ContactForm";

export default function Kvartsgulve() {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fallback images for Kvartsgulve
  const fallbackImages = [
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/image00001.jpeg",
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/image00002.jpeg",
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/image00003.jpeg"
  ];

  useEffect(() => {
    async function fetchGallery() {
      try {
        // Fetch all galleries and find the matching one to avoid 404 console errors
        const res = await fetch("https://api.dev.auxo.dk/public/content/gallery");
        if (res.ok) {
          const items = await res.json();
          const match = items.find(item => item.slug?.da === "kvartsgulve") || items.find(item => item.slug?.da === "industrigulve");
          if (match) {
            setGallery(match);
          }
        }
      } catch (err) {
        console.error("Fejl ved hentning af galleri fra CMS:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchGallery();
  }, []);

  const galleryImages = gallery?.images || fallbackImages;
  const galleryTitle = gallery?.title?.da || "Kvartsgulve & Industribelægninger";
  const galleryDesc = gallery?.description?.da || "Ekstremt slidstærke og skridsikre kvartsgulve til industri, storkøkkener og værksteder.";

  return (
    <>
      <Helmet>
        <title>Kvartsgulve Halsnæs | Skridsikre & Hygiejniske Gulve</title>
        <meta
          name="description"
          content="Slidstærke og skridsikre kvartsgulve i Halsnæs. Perfekt til storkøkkener, industri, slagtere og værksteder med høje krav til hygiejne og holdbarhed."
        />
        <meta property="og:title" content="Kvartsgulve Halsnæs | Skridsikre & Hygiejniske Gulve" />
        <meta
          property="og:description"
          content="Slidstærke og skridsikre kvartsgulve i Halsnæs. Perfekt til storkøkkener, industri, slagtere og værksteder med høje krav til hygiejne og holdbarhed."
        />
        <meta property="og:image" content={fallbackImages[0]} />
      </Helmet>

      {/* Page Header (Hero Style) */}
      <section className="bg-brand-dark text-white pt-48 pb-32 md:pt-44 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={fallbackImages[0]}
            alt="Kvartsgulv industrigulv"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/90 to-transparent"></div>
        
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs md:text-sm font-extrabold uppercase tracking-widest text-brand-orange mb-3 block">
              Slidstærke & Skridsikre Gulve
            </span>
            <h1 className="text-3xl md:text-5xl font-extrabold uppercase tracking-tight text-white leading-tight">
              Robuste Kvartsgulve
            </h1>
            <p className="text-base md:text-lg text-slate-300 mt-4 max-w-2xl leading-relaxed">
              Kvartsgulve (også kaldet farvekvartsgulve) er den ultimative løsning til områder med ekstremt slid, høje hygiejnekrav og behov for maksimal skridsikkerhed. Perfekt til storkøkkener, industri og værksteder.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#kontakt-form"
                className="inline-flex items-center justify-center bg-brand-orange text-white px-6 py-3 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-brand-orange/90 transition-all"
              >
                Få et tilbud
              </a>
              <a
                href={`tel:+45${CONTACT_INFO.phone}`}
                className="inline-flex items-center justify-center bg-white text-brand-dark px-6 py-3 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-slate-100 transition-all"
              >
                Ring {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Details */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            
            {/* Left: Detailed Copy */}
            <div className="lg:col-span-7 space-y-8">
              <div className="prose prose-slate max-w-none">
                <span className="text-xs md:text-sm font-extrabold uppercase tracking-widest text-brand-orange block mb-2">
                  Hvad er et kvartsgulv?
                </span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-brand-dark leading-snug mb-6">
                  Uovertruffen styrke og skridsikkerhed
                </h2>
                <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-6">
                  Et kvartsgulv opbygges ved at blande farvede kvartssandskorn med en klar epoxy- eller polyurethan-bindemiddel. Denne kombination skaber en ekstremt tæt, slidstærk og fugefri overflade, der kan modstå selv den mest intense mekaniske og kemiske belastning.
                </p>
                <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-6">
                  Kvartsgulve er især populære i erhverv og industri, hvor der stilles strenge krav til arbejdsmiljø og sikkerhed. Overfladen kan struktureres, så den opnår en høj skridsikkerhedsklassificering (op til R13), hvilket gør den ideel til vådrum, storkøkkener, slagterier og bilværksteder.
                </p>

                <h3 className="text-xl font-bold text-brand-dark mt-8 mb-4">
                  Fordele ved vores kvartsgulve:
                </h3>
                <ul className="space-y-3 my-6">
                  <li className="flex items-start gap-3 text-slate-600">
                    <i className="fa-solid fa-circle-check text-brand-orange mt-1"></i>
                    <span><strong>Maksimal skridsikkerhed:</strong> Strukturen tilpasses præcist til dit behov, så medarbejderne står fast – selv på våde eller fedtede gulve.</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <i className="fa-solid fa-circle-check text-brand-orange mt-1"></i>
                    <span><strong>Ekstrem slidstyrke:</strong> Modstandsdygtig over for tung kørsel med gaffeltrucks, palleløftere og maskiner.</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <i className="fa-solid fa-circle-check text-brand-orange mt-1"></i>
                    <span><strong>Hygiejnisk og fugefrit:</strong> Ingen samlinger, hvor skidt, bakterier og svamp kan samle sig. Nemt at spule og rengøre.</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <i className="fa-solid fa-circle-check text-brand-orange mt-1"></i>
                    <span><strong>Flot æstetik:</strong> Det farvede kvartssand giver et flot, nistret farvespil, der kan tilpasses i mange farvekombinationer.</span>
                  </li>
                </ul>

                <h3 className="text-xl font-bold text-brand-dark mt-8 mb-4">
                  Perfekt til storkøkkener og fødevareindustri
                </h3>
                <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-6">
                  I fødevarebranchen er kravene til hygiejne og rengøringsvenlighed kompromisløse. Vores kvartsgulve overholder alle gældende EU-direktiver og fødevarestyrelsens krav. Vi udfører gulvene med tætte, fugefrie hulkantslister (opkant på væggen), så vand og snavs ledes direkte mod gulvafløb under rengøring.
                </p>
              </div>

              {/* Dynamic Gallery Section */}
              <div className="pt-8 border-t border-slate-100">
                <h3 className="text-2xl font-extrabold text-brand-dark mb-2">
                  {galleryTitle}
                </h3>
                <p className="text-sm text-slate-500 mb-6">
                  {galleryDesc}
                </p>
                <GalleryLightbox images={galleryImages} />
              </div>
            </div>

            {/* Right: Sidebar & Contact Form */}
            <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-24" id="kontakt-form">
              <div className="bg-slate-50 p-8 rounded-lg border border-slate-100 shadow-sm">
                <span className="text-xs font-extrabold uppercase tracking-widest text-brand-orange block mb-2">
                  Få et gratis tilbud
                </span>
                <h3 className="text-2xl font-extrabold text-brand-dark mb-4">
                  Modtag uforpligtende tilbud
                </h3>
                <p className="text-sm text-slate-500 mb-6">
                  Udfyld formularen herunder, så vender Simon Babiel tilbage til dig hurtigst muligt med rådgivning og et skarpt tilbud.
                </p>
                <ContactForm source="Ydelsesside: Kvartsgulve" />
              </div>

              <div className="bg-brand-teal text-white p-8 rounded-lg">
                <h4 className="text-lg font-bold mb-2">Professionel rådgivning</h4>
                <p className="text-sm text-slate-200 mb-4">
                  Har du brug for rådgivning om skridsikkerhedsklasser (R9-R13) eller kemikalieresistens? Ring direkte til Simon Babiel.
                </p>
                <div className="flex flex-col gap-2">
                  <a
                    href={`tel:+45${CONTACT_INFO.phone}`}
                    className="flex items-center gap-2 text-sm font-bold hover:text-brand-orange transition-colors"
                  >
                    <i className="fa-solid fa-phone"></i>
                    Tlf: {CONTACT_INFO.phone}
                  </a>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="flex items-center gap-2 text-sm font-bold hover:text-brand-orange transition-colors break-all"
                  >
                    <i className="fa-solid fa-envelope"></i>
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Bottom CTA Block */}
      <section className="relative text-white py-24 overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/showroom-floor.jpg"
            alt="Slidstærkt kvartsgulv"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-brand-dark/60"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold uppercase tracking-tight mb-4">
            Skal vi lægge dit nye kvartsgulv?
          </h2>
          <p className="text-base text-slate-200 max-w-2xl mx-auto mb-8">
            Vi udfører slidstærke kvartsgulve for både erhverv, industri og private på hele Sjælland. Kontakt os i dag for en uforpligtende snak om dit projekt.
          </p>
          <a
            href="#kontakt-form"
            className="inline-flex items-center justify-center bg-brand-orange text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-brand-orange/90 transition-all"
          >
            Kontakt os nu
          </a>
        </div>
      </section>
    </>
  );
}
